import React, { useRef, useState, useEffect } from 'react';
import { MousePointer, Check, X, CheckSquare, Edit2, Save, ChevronDown, ChevronUp, Search, Plus, Trash2, GripVertical } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAllAssets } from '../../hooks/useAllAssets';
import {
  AdPreview,
  AudioPreview,
  DocumentPreview,
  ImagePreview,
  SocialPreview,
  TextPreview,
  VideoPreview,
  SEOPreview,
  FAQPreview,
  PDFPreview,
  VideoEditablePreview,
  AudioEditablePreview
} from './asset-previews';
import SocialMediaPreview from './asset-previews/SocialMediaPreview';
import AdsPreview from './asset-previews/ads/AdsPreview';
import LogoContestPreview from './asset-previews/LogoContestPreview';
import LogoContestManagement from './asset-previews/LogoContestManagement';

interface Asset {
  id: number;
  asset_type: string;
  file_name: string;
  content?: string;
  metadata?: any;
  asset_approvals?: any[];
}

interface FeedbackPin {
  id: string;
  x: number;
  y: number;
  comment: string;
  author?: string;
  timestamp: Date;
}

interface AssetPreviewProps {
  selectedAsset: Asset | null;
  feedbackPins: FeedbackPin[];
  isPlacingPin: boolean;
  tempPin: { x: number; y: number } | null;
  onPlacePin: (e: React.MouseEvent<HTMLDivElement>) => void;
  apiBaseUrl: string;
  reviewType: 'account_manager' | 'client';
  onApproveAsset?: (assetId: number) => void;
  onRejectAsset?: (assetId: number) => void;
  onEditAsset?: (assetId: number) => void;
  onApproveAll?: () => void;
  onSaveAsset?: (assetId: number, content: string) => Promise<void>;
  setPinComment?: (comment: string) => void;
  pinComment?: string;
  onAddPin?: () => void;
  onCancelPin?: () => void;
  contentType?: string;
  contentMetadata?: any;
  allAssets?: Asset[];
}

function AssetPreview({
  selectedAsset,
  feedbackPins,
  isPlacingPin,
  tempPin,
  onPlacePin,
  apiBaseUrl,
  reviewType,
  onApproveAsset,
  onRejectAsset,
  onEditAsset,
  onApproveAll,
  onSaveAsset,
  setPinComment,
  pinComment,
  onAddPin,
  onCancelPin,
  contentType,
  contentMetadata,
  allAssets = []
}: AssetPreviewProps) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [editingContent, setEditingContent] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [editingSeo, setEditingSeo] = useState(false);
  const [editedSeo, setEditedSeo] = useState<any>({});
  const [editingFaq, setEditingFaq] = useState(false);
  const [faqItems, setFaqItems] = useState<Array<{id: string, question: string, answer: string}>>([]);

  // Helper function to determine if an asset type should be directly editable
  const isDirectlyEditableAsset = (assetType: string) => {
    // Check if it's a directly editable type
    if (['copy', 'page', 'email', 'meta', 'faq', 'schema', 'transcription', 'podcast', 'song', 'social'].includes(assetType)) {
      return true;
    }
    // Also check if it's an audio file with "podcast" in the filename
    if (assetType === 'audio' && selectedAsset?.file_name?.toLowerCase().includes('podcast')) {
      return true;
    }
    return false;
  };
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveContent = async () => {
    if (!selectedAsset || !onSaveAsset) return;

    setIsSaving(true);
    try {
      await onSaveAsset(selectedAsset.id, editedText);
      setEditingContent(false);
      setEditedText('');
    } catch (error) {
      console.error('Failed to save content:', error);
      alert('Failed to save content. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingContent(false);
    setEditedText('');
  };

  // Initialize editedText for directly editable assets
  useEffect(() => {
    if (selectedAsset && isDirectlyEditableAsset(selectedAsset.asset_type)) {
      if (selectedAsset.asset_type === 'social') {
        // For social posts, initialize with the content or a default structure
        const socialText = selectedAsset.content ? selectedAsset.content.replace(/\\n/g, '\n') : 'Sample social media post';
        const socialContent = selectedAsset.metadata?.platforms || [
          { platform: 'twitter', text: socialText, hashtags: ['marketing', 'digital'] },
          { platform: 'linkedin', text: socialText, hashtags: ['business', 'growth'] },
          { platform: 'facebook', text: socialText, hashtags: ['community', 'social'] },
          { platform: 'instagram', text: socialText, hashtags: ['creative', 'design'] }
        ];
        setEditedText(JSON.stringify(socialContent, null, 2));
      } else {
        let content = selectedAsset.content || `Content for ${selectedAsset.file_name}`;
        // Convert \n to proper line breaks for display
        content = content.replace(/\\n/g, '\n').replace(/\n/g, '<br>');
        setEditedText(content);
      }
    }
  }, [selectedAsset]);

  // Rich text editor configuration - Google Docs style
  const quillModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ 'header': [1, 2, 3, false] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link']
      ],
      handlers: {}
    }
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline',
    'list', 'bullet', 'link'
  ];

  const getAssetStatus = () => {
    if (!selectedAsset?.asset_approvals) return 'pending';
    const currentApproval = selectedAsset.asset_approvals.find(
      (a: any) => a.approver_type === reviewType
    );
    return currentApproval?.status || 'pending';
  };

  const isTextAsset = (type: string) => {
    return ['page', 'copy', 'meta', 'seo', 'email', 'social', 'faq', 'ad_copy', 'search_ad', 'message_ad'].includes(type);
  };

  const isDigitalAdsContent = () => {
    // Check if this is a digital ads campaign by looking at metadata
    if (contentMetadata?.ad_platform) {
      return true;
    }
    // Also check content type for backwards compatibility
    return contentType && ['google_ads', 'facebook_ads', 'linkedin_ads', 'display_ads'].includes(contentType);
  };

  const renderContent = () => {
    if (!selectedAsset) {
      return (
        <div className="h-full flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <p className="text-gray-500 mb-2">Select an asset to preview</p>
            <p className="text-sm text-gray-400">Choose from the assets list</p>
          </div>
        </div>
      );
    }

    // Parse metadata helper
    const parseMetadata = (meta: any) => {
      if (!meta) return {};
      if (typeof meta === 'string') {
        try {
          return JSON.parse(meta);
        } catch {
          return {};
        }
      }
      return meta;
    };

    // Detect content type from metadata
    const detectContentType = () => {
      const metadata = parseMetadata(selectedAsset.metadata);
      const content = selectedAsset.content || '';

      // Check for ads/campaign content
      if (metadata.campaignName || metadata.adFormat ||
          metadata.google || metadata.facebook || metadata.linkedin ||
          metadata.display || metadata.ctv || metadata.radio ||
          metadata.type === 'ads' ||
          content.includes('"campaignName"') || content.includes('"adGroupName"')) {
        return 'ads';
      }

      return null;
    };

    // Parse and process metadata
    const metadata = parseMetadata(selectedAsset.metadata);

    // Try content-based detection first
    const detectedType = detectContentType() || metadata.type;

    // Asset structure analysis complete

    // Check if this is an ads asset with metadata
    if (detectedType === 'ads') {
      return (
        <AdsPreview
          asset={{
            ...selectedAsset,
            id: typeof selectedAsset.id === 'string' ? parseInt(selectedAsset.id) : selectedAsset.id,
            file_name: selectedAsset.file_name,
            asset_type: selectedAsset.asset_type || 'ads',
            content: selectedAsset.content,
            metadata: metadata
          }}
          apiBaseUrl={apiBaseUrl}
          onSave={onSaveAsset}
        />
      );
    }

    // For digital ads content, use the AdPreview component
    if (isDigitalAdsContent()) {
      // Determine the platform type from metadata or content type
      const platform = contentMetadata?.ad_platform || contentType;
      return (
        <AdPreview
          contentType={platform}
          assets={allAssets}
          contentMetadata={contentMetadata}
        />
      );
    }

    // Check for PDF by mime_type first
    if (selectedAsset.mime_type === 'application/pdf') {
      return (
        <DocumentPreview
          asset={selectedAsset}
          apiBaseUrl={apiBaseUrl}
        />
      );
    }

    const assetUrl = `${apiBaseUrl}/assets/${selectedAsset.id}/download`;

    switch (selectedAsset.asset_type) {
      case 'logo_contest':
        // For logo contest, we need to group all logo contest assets
        const logoContestAssets = allAssets.filter(a => a.asset_type === 'logo_contest');
        const LogoComponent = reviewType === 'account_manager' ? LogoContestManagement : LogoContestPreview;
        return (
          <LogoComponent
            assets={logoContestAssets.map(a => ({
              id: typeof a.id === 'string' ? parseInt(a.id) : a.id,
              file_name: a.file_name,
              metadata: a.metadata
            }))}
            apiBaseUrl={apiBaseUrl}
          />
        );

      case 'pdf':
        return (
          <PDFPreview
            asset={selectedAsset}
            apiBaseUrl={apiBaseUrl}
          />
        );

      case 'image':
      case 'infographic':
        return (
          <ImagePreview
            asset={selectedAsset}
            apiBaseUrl={apiBaseUrl}
            onSave={onSaveAsset}
          />
        );

      case 'video':
        return (
          <VideoEditablePreview
            asset={selectedAsset}
            apiBaseUrl={apiBaseUrl}
            onSave={onSaveAsset}
            isEditable={true}
          />
        );

      case 'audio':
      case 'podcast':
      case 'song':
        return (
          <AudioEditablePreview
            asset={selectedAsset}
            apiBaseUrl={apiBaseUrl}
            onSave={onSaveAsset}
            isEditable={true}
          />
        );

      case 'transcription':
        // Transcription should show a text editing interface
        const transcriptContent = selectedAsset.content || `Transcript for ${selectedAsset.file_name}`;

        return (
          <div className="h-full bg-white p-6 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Transcript
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveContent}
                    disabled={isSaving}
                    className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:bg-blue-300"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 disabled:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <div className="border border-gray-300 rounded-lg">
                <ReactQuill
                  value={editedText || transcriptContent}
                  onChange={setEditedText}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter transcript content here..."
                  style={{ height: '450px' }}
                />
              </div>
            </div>
          </div>
        );

      case 'social':
        // Use the enhanced SocialMediaPreview for social posts
        return (
          <SocialMediaPreview
            asset={selectedAsset}
            apiBaseUrl={apiBaseUrl}
            onSave={onSaveAsset}
          />
        );

      case 'copy':
      case 'page':
        const rawContent = selectedAsset.content || `Content for ${selectedAsset.file_name}`;
        // Check if content is HTML or plain text
        const isHTML = rawContent.includes('<') && rawContent.includes('>');
        // If plain text, replace \n with <br> for proper display
        const content = isHTML ? rawContent : rawContent.replace(/\\n/g, '\n');

        // Check if we're in editing mode
        if (editingContent) {
          return (
            <div className="h-full bg-white p-6 overflow-auto">
              <div className="max-w-4xl mx-auto">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Edit Content</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveContent}
                      disabled={isSaving}
                      className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:bg-blue-300"
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 disabled:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                <div className="border border-gray-300 rounded-lg google-docs-editor">
                  <ReactQuill
                    value={editedText || content}
                    onChange={setEditedText}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter your content here..."
                    style={{ height: 'calc(100vh - 300px)', minHeight: '400px' }}
                  />
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="h-full bg-white p-6 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => {
                    setEditedText(content);
                    setEditingContent(true);
                  }}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Content
                </button>
              </div>
              <div className="prose prose-lg">
                {isHTML ? (
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-gray-800">{content}</pre>
                )}
              </div>
            </div>
          </div>
        );

      case 'meta':
      case 'seo':
        // Use the new SEOPreview component
        try {
          const seoData = selectedAsset.metadata || selectedAsset.content ?
            (typeof selectedAsset.content === 'string' && selectedAsset.content ?
              JSON.parse(selectedAsset.content) : selectedAsset.metadata || {}) : {};

          return (
            <SEOPreview
              asset={{
                ...selectedAsset,
                metadata: seoData
              }}
              apiBaseUrl={apiBaseUrl}
              onSave={onSaveAsset}
            />
          );
        } catch (error) {
          console.error('Error rendering SEOPreview:', error);
          // Fallback to default rendering
          return (
            <div className="h-full bg-white p-6">
              <p className="text-red-500">Error rendering SEO preview</p>
              <pre>{JSON.stringify(selectedAsset, null, 2)}</pre>
            </div>
          );
        }

      case 'faq':
        // Use the new FAQPreview component
        try {
          const faqContent = selectedAsset.content || '[]';
          let faqs = [];
          try {
            faqs = JSON.parse(faqContent);
          } catch (e) {
            // Handle plain text FAQ format
            const lines = faqContent.split('\n').filter(l => l.trim());
            let currentQ = '';
            let currentA = '';
            lines.forEach(line => {
              if (line.startsWith('Q:') || line.startsWith('Question:')) {
                if (currentQ) {
                  faqs.push({
                    id: Date.now().toString() + Math.random(),
                    question: currentQ,
                    answer: currentA
                  });
                }
                currentQ = line.replace(/^(Q:|Question:)\s*/i, '').trim();
                currentA = '';
              } else if (line.startsWith('A:') || line.startsWith('Answer:')) {
                currentA = line.replace(/^(A:|Answer:)\s*/i, '').trim();
              } else if (currentA) {
                currentA += ' ' + line;
              }
            });
            if (currentQ) {
              faqs.push({
                id: Date.now().toString() + Math.random(),
                question: currentQ,
                answer: currentA
              });
            }
          }

          return (
            <FAQPreview
              asset={{
                ...selectedAsset,
                metadata: {
                  faqs: faqs
                }
              }}
              apiBaseUrl={apiBaseUrl}
              onSave={onSaveAsset}
            />
          );
        } catch (error) {
          console.error('Error rendering FAQPreview:', error);
          // Fallback to default rendering
          return (
            <div className="h-full bg-white p-6">
              <p className="text-red-500">Error rendering FAQ preview</p>
              <pre>{JSON.stringify(selectedAsset, null, 2)}</pre>
            </div>
          );
        }

      case 'email':
        return (
          <TextPreview
            asset={selectedAsset}
            apiBaseUrl={apiBaseUrl}
            onSave={onSaveAsset}
          />
        );

      case 'schema':
        const textContent = selectedAsset.content || `Content for ${selectedAsset.file_name}`;

        // For text-based assets, always show edit interface by default
        return (
          <div className="h-full bg-white p-6 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit Schema Content</h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveContent}
                    disabled={isSaving}
                    className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:bg-blue-300"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 disabled:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <div className="border border-gray-300 rounded-lg">
                <ReactQuill
                  value={editedText || textContent}
                  onChange={setEditedText}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter schema content here..."
                  style={{ height: 'calc(100vh - 350px)', minHeight: '400px' }}
                />
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="h-full bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-500">Preview not available for {selectedAsset.asset_type}</p>
              <p className="text-sm text-gray-400 mt-2">{selectedAsset.file_name}</p>
            </div>
          </div>
        );
    }
  };

  const status = selectedAsset ? getAssetStatus() : 'pending';
  const isText = selectedAsset ? isTextAsset(selectedAsset.asset_type) : false;

  return (
    <div className="h-full flex flex-col">
      {/* Preview Area */}
      <div
        ref={previewRef}
        className="flex-1 relative bg-white overflow-auto"
        onClick={onPlacePin}
        style={{ cursor: isPlacingPin ? 'crosshair' : 'default' }}
      >
        {renderContent()}

        {/* Feedback Pins */}
        {feedbackPins.map((pin) => (
          <div
            key={pin.id}
            className="absolute w-6 h-6 bg-blue-500 rounded-full border-2 border-white shadow-lg cursor-pointer hover:scale-110 transition-transform z-10"
            style={{ left: `${pin.x}%`, top: `${pin.y}%`, transform: 'translate(-50%, -50%)' }}
            title={pin.comment}
          >
            <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
              {pin.comment}
            </span>
          </div>
        ))}

        {/* Temp Pin */}
        {tempPin && (
          <div
            className="absolute w-6 h-6 bg-red-500 rounded-full border-2 border-white shadow-lg animate-pulse z-10"
            style={{ left: `${tempPin.x}%`, top: `${tempPin.y}%`, transform: 'translate(-50%, -50%)' }}
          />
        )}
      </div>
    </div>
  );
}

export default AssetPreview;